import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/features/auth/auth-context'
import { supabase } from '@/lib/supabase'
import type { UserRole } from '@/types/profile'

const MFA_SESSION_KEY = 'zero_mfa_verified_at'
const TRUSTED_DEVICE_KEY = 'zero_mfa_trusted_device'

/**
 * How long a non-system_admin MFA session lasts before re-verification is required.
 * 4 hours in milliseconds.
 */
const MFA_SESSION_DURATION_MS = 4 * 60 * 60 * 1000 // 14_400_000

/**
 * Check whether the device is trusted (remembered) for a specific factor.
 *
 * When the user ticks "Remember this device for 30 days" during MFA
 * verification, the device trust is stored in `localStorage` so it
 * persists across browser sessions.
 */
function isTrustedDevice(factorId: string | null): boolean {
  if (!factorId) return false

  try {
    const stored = localStorage.getItem(TRUSTED_DEVICE_KEY)
    if (!stored) return false

    const trust = JSON.parse(stored) as { factorId?: string; expiresAt?: number }
    if (trust.factorId !== factorId) return false
    if (typeof trust.expiresAt !== 'number') return false

    return Date.now() < trust.expiresAt
  } catch {
    return false
  }
}

/**
 * Check whether the MFA session is still valid for a given role.
 *
 * - `system_admin` always requires MFA on every login (session is never valid
 *   from a previous verification).
 * - All other roles are valid for 4 hours after the last MFA verification.
 * - The timestamp lives in `sessionStorage` so it is automatically cleared
 *   when the browser tab/window is closed.
 */
function isMfaSessionValid(role: UserRole): boolean {
  if (role === 'system_admin') {
    return false
  }

  try {
    const stored = sessionStorage.getItem(MFA_SESSION_KEY)
    if (!stored) return false

    const verifiedAt = new Date(stored).getTime()
    if (Number.isNaN(verifiedAt)) return false

    return Date.now() - verifiedAt < MFA_SESSION_DURATION_MS
  } catch {
    return false
  }
}

/**
 * Record that MFA was successfully verified right now.
 */
function markMfaVerified(): void {
  try {
    sessionStorage.setItem(MFA_SESSION_KEY, new Date().toISOString())
  } catch {
    // sessionStorage may not be available (e.g. private browsing on some browsers)
  }
}

/**
 * Remove the MFA verification timestamp (e.g. on sign-out).
 */
function clearMfaSession(): void {
  try {
    sessionStorage.removeItem(MFA_SESSION_KEY)
  } catch {
    // Non-critical
  }
}

interface UseMfaSessionReturn {
  /** Whether the current user needs to (re-)verify MFA before proceeding. */
  isMfaRequired: boolean
  /** The verified TOTP factor ID, if the user has MFA enrolled. Null if none. */
  factorId: string | null
  /** Mark the current MFA challenge as successfully verified. */
  markMfaVerified: () => void
  /** Clear the MFA session (call on sign-out). */
  clearMfaSession: () => void
  /** True while the hook is loading factor/role data. */
  loading: boolean
}

/**
 * Hook that tracks MFA session validity for the current user.
 *
 * It fetches the user's role from the `profiles` table and their TOTP
 * factor status from Supabase Auth, then determines whether re-verification
 * is needed.
 *
 * The MFA verification timestamp is stored in `sessionStorage` so it
 * automatically expires when the browser is closed.
 */
export function useMfaSession(): UseMfaSessionReturn {
  const { user } = useAuth()
  const [role, setRole] = useState<UserRole | null>(null)
  const [factorId, setFactorId] = useState<string | null>(null)
  const [hasMfa, setHasMfa] = useState(false)
  const [loading, setLoading] = useState(true)

  // Fetch the user's role and MFA factor status
  useEffect(() => {
    let mounted = true

    async function fetchData() {
      if (!user) {
        if (mounted) {
          setRole(null)
          setFactorId(null)
          setHasMfa(false)
          setLoading(false)
        }
        return
      }

      try {
        // Fetch role and MFA factors in parallel
        const [profileResult, factorsResult] = await Promise.all([
          supabase.from('profiles').select('role').eq('id', user.id).single(),
          supabase.auth.mfa.listFactors(),
        ])

        if (!mounted) return

        if (profileResult.data) {
          setRole(profileResult.data.role as UserRole)
        }

        const verifiedFactor = factorsResult.data?.totp?.find((f) => f.status === 'verified')
        setHasMfa(!!verifiedFactor)
        setFactorId(verifiedFactor?.id ?? null)
      } catch {
        // Default to safe state -- require MFA if we can't determine status
        if (mounted) {
          setHasMfa(false)
          setFactorId(null)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    setLoading(true)
    fetchData()

    return () => {
      mounted = false
    }
  }, [user])

  const handleMarkVerified = useCallback(() => {
    markMfaVerified()
    // Force a re-render so consumers immediately see isMfaRequired=false
    setRole((prev) => prev)
  }, [])

  // Determine whether MFA re-verification is needed.
  // Not required when: user has a valid session OR a trusted device (non-admin).
  const sessionValid = role !== null && isMfaSessionValid(role)
  const deviceTrusted = role !== 'system_admin' && isTrustedDevice(factorId)
  const isMfaRequired = hasMfa && role !== null && !sessionValid && !deviceTrusted

  return {
    isMfaRequired,
    factorId,
    markMfaVerified: handleMarkVerified,
    clearMfaSession,
    loading,
  }
}

export { clearMfaSession, markMfaVerified }
